import { recordPlanningRevisionResponse, recordPlanningRevisionIgnored } from "../utils/planningRevisionDiagnostics.js";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { isDiagnosticsActive, recordDiagnosticsEvent } from "./useDiagnostics.js";

const pageDataCache = new Map();
const inFlightPageDataCache = new Map();
const pageDataCacheGenerations = new Map();

export class PageDataRequestError extends Error {
  constructor(message, { status = null, reasonCode = "", retryable = false, requestId = "", retryAfterMs = 1000 } = {}) {
    super(message);
    this.name = "PageDataRequestError";
    this.status = Number.isInteger(status) ? status : null;
    this.reasonCode = reasonCode;
    this.retryable = retryable;
    this.requestId = requestId;
    this.retryAfterMs = retryAfterMs;
  }
}

export function isPageDataAuthError(error) {
  return error instanceof PageDataRequestError && error.status === 401;
}

export function getPageDataDiagnosticsSnapshot() {
  return {
    cacheKeyCount: pageDataCache.size,
    cacheKeys: [...pageDataCache.keys()],
    cacheGenerationPrefixes: [...pageDataCacheGenerations.keys()],
    inFlightKeys: [...inFlightPageDataCache.keys()],
    inFlightRequestCount: inFlightPageDataCache.size,
  };
}

// PHASE 1.5 TEMP: diagnostic local des cardinalites cache/in-flight page data.
function logPhase15PageDataSnapshot(event, details = {}) {
  if (!isDiagnosticsActive()) {
    return;
  }

  recordDiagnosticsEvent("usePageData", event, {
    ...details,
    ...getPageDataDiagnosticsSnapshot(),
    pageDataCacheSize: pageDataCache.size,
    inFlightPageDataCacheSize: inFlightPageDataCache.size,
  }, {
    countAsReset: event === "cache_invalidate",
  });
}

function normalizeAcceptableStatusCodes(statusCodes) {
  if (!Array.isArray(statusCodes)) {
    return [];
  }

  return [...new Set(statusCodes.filter((statusCode) => Number.isInteger(statusCode) && statusCode >= 100))];
}

function normalizePageDataUrls(apiUrls) {
  if (!Array.isArray(apiUrls)) {
    return [];
  }

  return [...new Set(apiUrls.filter((apiUrl) => typeof apiUrl === "string" && apiUrl))];
}

function getPageDataCachePrefix(apiUrl) {
  if (typeof apiUrl !== "string" || !apiUrl) {
    return "";
  }

  const queryIndex = apiUrl.indexOf("?");
  const hashIndex = apiUrl.indexOf("#");
  let endIndex = apiUrl.length;

  if (queryIndex >= 0) {
    endIndex = Math.min(endIndex, queryIndex);
  }
  if (hashIndex >= 0) {
    endIndex = Math.min(endIndex, hashIndex);
  }

  return apiUrl.slice(0, endIndex);
}

function readCacheGeneration(prefix) {
  return pageDataCacheGenerations.get(prefix) || 0;
}

function collectCacheGenerationPrefixes(apiUrl) {
  const prefixes = new Set();
  const safeApiUrl = typeof apiUrl === "string" ? apiUrl : "";

  if (safeApiUrl) {
    prefixes.add(safeApiUrl);
    prefixes.add(getPageDataCachePrefix(safeApiUrl));
  }

  collectPageDataCacheKeysForInvalidation(safeApiUrl).forEach((cachedApiUrl) => {
    prefixes.add(cachedApiUrl);
    prefixes.add(getPageDataCachePrefix(cachedApiUrl));
  });

  return [...prefixes].filter(Boolean);
}

function bumpPageDataCacheGenerations(apiUrls) {
  const bumpedPrefixes = new Set();

  normalizePageDataUrls(apiUrls).forEach((apiUrl) => {
    collectCacheGenerationPrefixes(apiUrl).forEach((prefix) => {
      bumpedPrefixes.add(prefix);
    });
  });

  bumpedPrefixes.forEach((prefix) => {
    pageDataCacheGenerations.set(prefix, readCacheGeneration(prefix) + 1);
  });

  return [...bumpedPrefixes];
}

function isPageDataCacheWriteStillValid(prefix, generationAtStart) {
  return readCacheGeneration(prefix) === generationAtStart;
}

function collectPageDataCacheKeysForInvalidation(apiUrl) {
  if (typeof apiUrl !== "string" || !apiUrl) {
    return [];
  }

  const relatedCacheKeys = [];
  for (const cachedApiUrl of pageDataCache.keys()) {
    if (cachedApiUrl === apiUrl || cachedApiUrl.startsWith(`${apiUrl}?`) || cachedApiUrl.startsWith(`${apiUrl}#`)) {
      relatedCacheKeys.push(cachedApiUrl);
    }
  }

  if (relatedCacheKeys.length === 0) {
    return [apiUrl];
  }

  return [...new Set(relatedCacheKeys)];
}

export function invalidatePageDataCache(apiUrl) {
  return invalidatePageDataCacheEntries([apiUrl]);
}

export function invalidatePageDataCacheEntries(apiUrls) {
  const normalizedUrls = normalizePageDataUrls(apiUrls);
  if (normalizedUrls.length === 0) {
    return [];
  }

  const invalidatedUrls = new Set();
  normalizedUrls.forEach((apiUrl) => {
    collectPageDataCacheKeysForInvalidation(apiUrl).forEach((cachedApiUrl) => {
      pageDataCache.delete(cachedApiUrl);
      invalidatedUrls.add(cachedApiUrl);
    });
  });

  const bumpedPrefixes = bumpPageDataCacheGenerations(normalizedUrls);

  logPhase15PageDataSnapshot("cache_invalidate", {
    bumpedPrefixes,
    invalidatedUrls: [...invalidatedUrls],
  });

  return [...invalidatedUrls];
}

export function clearPageDataCache() {
  const trackedApiUrls = new Set([
    ...pageDataCache.keys(),
    ...pageDataCacheGenerations.keys(),
  ]);

  for (const requestKey of inFlightPageDataCache.keys()) {
    try {
      const requestDetails = JSON.parse(requestKey);
      if (typeof requestDetails?.apiUrl === "string" && requestDetails.apiUrl) {
        trackedApiUrls.add(requestDetails.apiUrl);
      }
    } catch {}
  }

  const invalidatedUrls = invalidatePageDataCacheEntries([...trackedApiUrls]);
  pageDataCache.clear();
  inFlightPageDataCache.clear();
  return invalidatedUrls;
}

function hasCachedPageData(apiUrl) {
  return typeof apiUrl === "string" && pageDataCache.has(apiUrl);
}

function readCachedPageData(apiUrl) {
  return hasCachedPageData(apiUrl) ? pageDataCache.get(apiUrl) : null;
}

function writeCachedPageData(apiUrl, nextData, { generationAtStart = null } = {}) {
  if (typeof apiUrl !== "string" || !apiUrl) {
    return nextData;
  }

  const cachePrefix = getPageDataCachePrefix(apiUrl);
  if (
    Number.isInteger(generationAtStart)
    && !isPageDataCacheWriteStillValid(cachePrefix, generationAtStart)
  ) {
    logPhase15PageDataSnapshot("cache_write_skipped_stale", {
      apiUrl,
      cachePrefix,
      generationAtStart,
      currentGeneration: readCacheGeneration(cachePrefix),
    });
    const cachedData = readCachedPageData(apiUrl);
    if (cachePrefix === "/api/results/latest" && cachedData != null && cachedData !== nextData) {
      recordPlanningRevisionIgnored(nextData, "cache_generation_replaced");
    }
    return cachedData ?? nextData;
  }

  pageDataCache.set(apiUrl, nextData);
  logPhase15PageDataSnapshot("cache_write", { apiUrl, cachePrefix });
  return nextData;
}

function buildPageDataRequestKey(apiUrl, acceptableStatusCodes = []) {
	return JSON.stringify({
		acceptableStatusCodes: normalizeAcceptableStatusCodes(acceptableStatusCodes),
		apiUrl,
		generation: readCacheGeneration(getPageDataCachePrefix(apiUrl)),
	});
}

function isAbortError(error) {
  const hasDomException = typeof DOMException !== "undefined";
  return Boolean(
    hasDomException && error instanceof DOMException && error.name === "AbortError"
    || error && typeof error === "object" && error.name === "AbortError"
  );
}

export function writePageDataCacheEntry(apiUrl, nextData) {
	bumpPageDataCacheGenerations([apiUrl]);
	return writeCachedPageData(apiUrl, nextData);
}

async function readPageDataRequestError(response, signal) {
  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    if (isAbortError(error)) throw error;
  }
  signal?.throwIfAborted();
  const detail = payload?.detail;
  const message = [detail?.error, detail?.message, detail, payload?.error, payload?.message]
    .find((value) => typeof value === "string" && value.trim());
  const retryAfter = response.headers?.get?.("Retry-After")?.trim();
  const retryAfterMs = !retryAfter ? 1000
    : /^\d+(?:\.\d+)?$/.test(retryAfter) ? Number(retryAfter) * 1000
      : Math.max(0, Date.parse(retryAfter) - Date.now());
  return new PageDataRequestError(message?.trim() || (response.status === 503
    ? "Le service est temporairement indisponible. Réessayez dans un instant."
    : `Erreur ${response.status} : ${response.statusText || "le chargement a échoué."}`), {
    status: response.status,
    reasonCode: detail?.reason_code || payload?.reason_code || "",
    retryable: (detail?.retryable ?? payload?.retryable) === true,
    requestId: response.headers?.get?.("X-Request-ID") || response.headers?.get?.("X-Railway-Request-Id") || "",
    retryAfterMs: Number.isFinite(retryAfterMs) ? retryAfterMs : 1000,
  });
}

function waitForPageDataRetry(delayMs, signal) {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

async function fetchPageData(apiUrl, { acceptableStatusCodes = [], signal } = {}) {
  signal?.throwIfAborted();
  if (typeof apiUrl !== "string" || !apiUrl) {
    throw new Error("Impossible de charger une page sans endpoint API valide.");
  }

	const normalizedAcceptableStatusCodes = normalizeAcceptableStatusCodes(acceptableStatusCodes);
	const requestKey = buildPageDataRequestKey(apiUrl, normalizedAcceptableStatusCodes);
	const canReuseInFlightRequest = !signal;
	const existingRequest = canReuseInFlightRequest ? inFlightPageDataCache.get(requestKey) : null;
	if (existingRequest) {
		logPhase15PageDataSnapshot("request_reused", { apiUrl, requestKey });
		return existingRequest;
	}

  const cachePrefix = getPageDataCachePrefix(apiUrl);
  const generationAtStart = readCacheGeneration(cachePrefix);

  const requestPromise = (async () => {
    for (let attempt = 0; ; attempt += 1) {
      const response = await fetchWithSupabaseAuth(apiUrl, { signal });
      signal?.throwIfAborted();
      const isAcceptedResponse = response.ok || normalizedAcceptableStatusCodes.includes(response.status);
      if (!isAcceptedResponse) {
        const error = await readPageDataRequestError(response, signal);
        // Retry only this read and only when the server explicitly permits it.
        // A longer Retry-After remains a manual retry; never retry earlier.
        if (cachePrefix === "/api/results/latest" && error.status === 503
          && error.retryable && attempt < 2 && error.retryAfterMs <= 5000) {
          logPhase15PageDataSnapshot("request_retry", {
            apiUrl, attempt: attempt + 1, reasonCode: error.reasonCode, requestId: error.requestId,
          });
          await waitForPageDataRetry(error.retryAfterMs, signal);
          continue;
        }
        throw error;
      }

      let json = null;
      try {
        json = await response.json();
      } catch (error) {
        signal?.throwIfAborted();
        if (isAbortError(error)) {
          throw error;
        }
        throw new Error(`Erreur ${response.status} : le serveur n'a pas renvoyé de données JSON exploitables.`);
      }

      if (cachePrefix === "/api/results/latest") {
        recordPlanningRevisionResponse(json, response, { url: apiUrl });
        if (signal?.aborted) recordPlanningRevisionIgnored(json, "aborted");
      }
      signal?.throwIfAborted();
      return writeCachedPageData(apiUrl, json, { generationAtStart });
    }
  })()
    .finally(() => {
      if (inFlightPageDataCache.get(requestKey) === requestPromise) {
        inFlightPageDataCache.delete(requestKey);
      }
      logPhase15PageDataSnapshot("request_settled", { apiUrl, requestKey });
    });

  if (canReuseInFlightRequest) {
    inFlightPageDataCache.set(requestKey, requestPromise);
  }
  logPhase15PageDataSnapshot("request_started", { acceptableStatusCodes: normalizedAcceptableStatusCodes, apiUrl, requestKey });
  return requestPromise;
}

export async function loadPageData(apiUrl, { acceptableStatusCodes = [], force = false, signal } = {}) {
  signal?.throwIfAborted();
  if (!force) {
    const cachedData = readCachedPageData(apiUrl);
    if (cachedData != null) {
      return cachedData;
    }
  }

  return fetchPageData(apiUrl, {
    acceptableStatusCodes,
    signal,
  });
}

export function createPageLoader(apiUrl, options = {}) {
  return async function pageLoader({ request } = {}) {
    return loadPageData(apiUrl, {
      acceptableStatusCodes: options.acceptableStatusCodes,
      signal: options.abortable === true ? request?.signal : undefined,
    });
  };
}
